import { roundDecimal } from '@autosign/utils'
import { useRef } from 'react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from 'ui'
import { PDField, usePDFStore } from '../../../../stores/pdf.store'
import { findBoundaryFromField } from '../../../../utils/findBoundaryFromField'
import { FieldItem } from '../../../Canvas/FieldsEditLayer/FieldItem'
import { buildPagePortalId } from '../../../PDFViewer'
import { TunnelRat } from '../../../TunnelRat'
import { useSigningForm } from '../Provider'

export const SigningFormFieldPlaceholder = ({
  field,
  children,
}: {
  field: PDField
  children: React.ReactNode
}) => {
  const pageBoundary = usePDFStore((state) =>
    findBoundaryFromField(state, field)
  )
  const updateField = usePDFStore((state) => state.updateField)
  const updateStore = usePDFStore((state) => state.update)
  const currentFocusFieldId = usePDFStore((state) => state.currentFocusFieldId)

  const isFocus = currentFocusFieldId === field.id

  const color = usePDFStore(
    (state) => state.recipients.find((r) => r.id === field.recipient_id)!.color
  )

  const form = useSigningForm()

  const triggerRef = useRef<HTMLDivElement>(null)

  const tunnelId = buildPagePortalId(field.document_id, field.page)

  console.log('tunnelId', tunnelId, 'pageBoundary', pageBoundary)

  const errMessage = form.formState?.errors?.fields?.[field.id]?.message

  if (!pageBoundary) return null

  return (
    <TunnelRat tunnelId={tunnelId}>
      {({ element }) => {
        const errMessage = form.formState?.errors?.fields?.[field.id]?.message

        return (
          <TooltipProvider delayDuration={0}>
            <Tooltip
              onOpenChange={(open) => {
                if (open) updateStore('currentFocusFieldId', field.id)
              }}
            >
              <TooltipTrigger asChild>
                <FieldItem
                  fontColor={field.font_color}
                  fontSize={field.font_size}
                  fontFamily={field.font_family}
                  onClick={(e) => e.preventDefault()}
                  className={cn(
                    'absolute cursor-pointer p-0 border-none m-0 ring-2 ring-primary truncate flex items-start z-10',
                    {
                      'ring-2 ring-black': isFocus,
                      'ring-2 ring-red-500': !!errMessage,
                      'italic *:!italic': field.italic,
                      'font-bold *:!font-bold': field.bold,
                      '*:!underline': field.underline,
                      '[&>div>*]:text-right [&>div>div>*]:text-right [&>div>div>div>*]:text-right':
                        field.alignment === 'right',
                      '[&>div>*]:text-left [&>div>div>*]:text-left [&>div>div>div>*]:text-left':
                        field.alignment === 'left',
                      '[&>div>*]:text-center [&>div>div>*]:text-center [&>div>div>div>*]:text-center':
                        field.alignment === 'center',
                    }
                  )}
                  style={{
                    width: roundDecimal(
                      (+field.width / 100) * pageBoundary.width
                    ),
                    height: (+field.height / 100) * pageBoundary.height,
                    position: 'absolute',
                    left: roundDecimal(
                      (+field.positionX / 100) * pageBoundary.width
                    ),
                    top: roundDecimal(
                      (+field.positionY / 100) * pageBoundary.height
                    ),
                  }}
                  ref={triggerRef}
                >
                  {children}
                </FieldItem>
              </TooltipTrigger>
              <TooltipContent
                onPointerDownOutside={(event) => {
                  if (triggerRef.current?.contains(event.target))
                    event.preventDefault()
                }}
                align='start'
              >
                <div>
                  <p
                    className={cn({
                      required: field.is_required,
                    })}
                  >
                    {field.label}
                  </p>
                  {field.helper_text && (
                    <p className='text-muted-foreground text-xs'>
                      {field.helper_text}
                    </p>
                  )}
                  {errMessage && typeof errMessage === 'string' && (
                    <p className='text-red-500'>{errMessage}</p>
                  )}
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )
      }}
    </TunnelRat>
  )
}
